import type { ClassicEditor } from 'ckeditor5'
import { Plugin } from 'ckeditor5'

/**
 * Helper to check if editor content is empty
 */
const isEmptyContent = (data: string): boolean => {
    return !data || data === '<p></p>' || data.trim() === ''
}

/**
 * Custom CKEditor plugin to handle caret visibility in empty content
 */
export class EmptyContentCaretPlugin extends Plugin {
    static get pluginName() {
        return 'EmptyContentCaretPlugin'
    }

    init() {
        const editor = this.editor as ClassicEditor
        const view = editor.editing.view

        // Handle focus events - only update the class, don't change selection
        view.document.on('focus', () => {
            this.updateEmptyContentClass()
        })

        // Also handle when view renders (to maintain caret for empty content only)
        view.on('render', () => {
            const data = editor.getData()
            if (isEmptyContent(data) && view.document.isFocused) {
                this.maintainEmptyContentCaret()
            }
            // Don't interfere with non-empty content
        })

        // Handle content changes
        editor.model.document.on('change:data', () => {
            this.updateEmptyContentClass()
        })

        // Handle initial state
        editor.on('ready', () => {
            this.updateEmptyContentClass()
        })
    }

    /**
     * Updates the empty content class based on content state
     */
    private updateEmptyContentClass() {
        const editor = this.editor as ClassicEditor
        const editableElement = editor.editing.view.getDomRoot()

        if (editableElement) {
            const data = editor.getData()

            if (isEmptyContent(data)) {
                editableElement.classList.add('ck-empty-content')
            } else {
                editableElement.classList.remove('ck-empty-content')
            }
        }
    }

    /**
     * Maintains the caret visibility for empty content
     */
    private maintainEmptyContentCaret() {
        const editor = this.editor as ClassicEditor
        const editableElement = editor.editing.view.getDomRoot()

        if (
            editableElement &&
            !editableElement.classList.contains('ck-empty-content')
        ) {
            editableElement.classList.add('ck-empty-content')
        }
    }
}
